import React from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";

import { useIconCopy } from "frontend/hooks/useIconDownload";
import { useAuth } from "frontend/hooks/useAuth";

import { CopyButton as StyledCopyButton } from "./styles";

const CopyButton = ({ icon, editorValues = {} }) => {
    const { confirmAuth } = useAuth();
    const {
        copy,
        browserCanCopy,
        isStarted,
        isCompleted,
        hasFailed
    } = useIconCopy();

    if (!browserCanCopy) {
        return null;
    }

    const copyLabel = hasFailed ? "Failed" : isCompleted ? "Copied" : "Copy";

    const onClick = () =>
        confirmAuth({
            async onSuccess(currentUser) {
                /* To pass security rules, Safari requires that `navigator.clipboard.write`
                 * is called synchronously after a user action. Async work is done by giving
                 * `ClipboardItem` a promise that eventually returns a blob.
                 *
                 * Other browsers support `navigator.clipboard.write` being called async,
                 * but require giving `ClipboardItem` a blob and not a promise.
                 */
                try {
                    let blob = copy({
                        currentUser,
                        iconId: icon.id,
                        editorValues
                    });
                    // `vendor` targets all Safari based browsers, including iOS Chrome / FF / Brave
                    if (window.navigator.vendor !== "Apple Computer, Inc.") {
                        blob = await blob;

                        // If the download failed, blob will be `null` and we should skip copying
                        if (!blob) return;
                    }
                    await window.navigator.clipboard.write([
                        new window.ClipboardItem({
                            ["image/png"]: blob
                        })
                    ]);
                } catch (e) {
                    /* If the user has to log in first, Safari will complain because clipboard is
                     * being called async. Ignore this error. But send all other errors to Sentry.
                     */
                    if (e.name !== "NotAllowedError") {
                        Sentry.captureException(
                            `Failed to copy icon to clipboard. Error: ${e}`
                        );
                    }
                }
            }
        });

    return (
        <StyledCopyButton isDisabled={isStarted} onClick={onClick}>
            {copyLabel}
        </StyledCopyButton>
    );
};

CopyButton.propTypes = {
    icon: PropTypes.object.isRequired,
    editorValues: PropTypes.object
};

export default CopyButton;
